// Bundle parents reject parent_id. Nest keys match hybrid-cart.
function nestBundleItems(items) {
  if (!items.some((item) => item.parent_id != null)) return items
  const nestId = crypto.randomUUID()
  return items.map((item) => {
    if (item.parent_id == null) {
      return { ...item, properties: { ...(item.properties || {}), _HC_NEST_ID: nestId } }
    }
    const { parent_id, ...rest } = item
    return { ...rest, properties: { ...(rest.properties || {}), _HC_PARENT_KEY: nestId } }
  })
}

class SubscriptionProductForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return
    this.initialized = true
    this.variants = this.readJson('#product-variants-data', [])
    this.formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      trailingZeroDisplay: 'stripIfInteger',
    })
    this.setupEvents()
    this.update()
  }

  readJson(selector, fallback) {
    const el = this.querySelector(selector)
    if (!el) return fallback
    try {
      const parsed = JSON.parse(el.textContent || 'null')
      return parsed == null ? fallback : parsed
    } catch (error) {
      console.error('[subscription-product-form] Failed to parse JSON', selector, error)
      return fallback
    }
  }

  setupEvents() {
    this.querySelectorAll('input[name="purchase-type"]').forEach((input) => {
      input.addEventListener('change', () => this.update())
    })

    this.querySelectorAll('[data-product-options] input').forEach((input) => {
      input.addEventListener('change', () => this.update())
    })

    this.querySelectorAll('[data-add-to-cart-one-time]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        if (button.disabled || button.dataset.loading != null) return
        const variant = this.getOneTimePackVariant()
        if (!variant || this.hasAttribute('data-force-out-of-stock') || variant.available === false) return
        button.dataset.loading = ''
        this.addToCart(button, { oneTime: true })
      })
    })

    this.querySelectorAll('select[name="selling_plan"]').forEach((select) => {
      select.addEventListener('focus', () => this.selectSubscription())
      select.addEventListener('pointerdown', () => this.selectSubscription())
      select.addEventListener('change', () => this.update())
    })

    this.querySelectorAll('[name="addon-product"]').forEach((input) => {
      input.addEventListener('change', () => this.update())
    })

    this.setupAddToCart()
  }

  isOwnedAddToCartButton(button) {
    if (this.contains(button)) return true
    return Boolean(button.closest('#pdp-sticky-nav, #pdp-sticky-nav-overlay, #pdp-mobile-cta'))
  }

  setupAddToCart() {
    // Capture-phase document listener: sticky nav buttons live outside this
    // element (#pdp-sticky-nav, #pdp-sticky-nav-overlay, #pdp-mobile-cta).
    // Only those plus this form's ATC are owned; other ATC controls are ignored.
    this.onDocumentAddToCartClick =
      this.onDocumentAddToCartClick ||
      ((e) => {
        if (!(e.target instanceof Element)) return
        const button = e.target.closest('[data-add-to-cart]')
        if (!button || !this.isOwnedAddToCartButton(button)) return
        e.preventDefault()
        e.stopImmediatePropagation()
        if (button.disabled || button.dataset.loading != null) return
        const variant = this.getSelectedVariant()
        if (!variant || this.hasAttribute('data-force-out-of-stock') || variant.available === false) return
        button.dataset.loading = ''
        this.addToCart(button)
      })
    document.addEventListener('click', this.onDocumentAddToCartClick, true)
  }

  disconnectedCallback() {
    if (this.onDocumentAddToCartClick) {
      document.removeEventListener('click', this.onDocumentAddToCartClick, true)
    }
  }

  isQuantityFirst() {
    return this.hasAttribute('data-quantity-first')
  }

  isSubscription() {
    if (this.isQuantityFirst()) return true
    const selected = this.querySelector('input[name="purchase-type"]:checked')
    if (!selected) return this.querySelector('input[name="purchase-type"][value="subscription"]') != null
    return selected.value === 'subscription'
  }

  selectSubscription() {
    const radio = this.querySelector('input[name="purchase-type"][value="subscription"]')
    if (!radio || radio.checked) return
    radio.checked = true
    this.update()
  }

  getSelectedVariant() {
    const optionInput = this.querySelector('[data-product-options] input:checked')
    if (optionInput) {
      const variantId = Number.parseInt(optionInput.value, 10)
      const match = this.variants.find((variant) => variant.id === variantId)
      if (match) return match
    }
    const attrId = Number.parseInt(this.getAttribute('variant-id'), 10)
    return this.variants.find((variant) => variant.id === attrId) || this.variants[0] || null
  }

  getOneTimePackVariant() {
    const onePackInput = this.querySelector('[data-product-options] input[data-quantity-months="1"]')
    if (onePackInput) {
      const variantId = Number.parseInt(onePackInput.value, 10)
      const match = this.variants.find((variant) => variant.id === variantId)
      if (match) return match
    }

    return (
      this.variants.find((variant) => {
        const title = (variant.title || '').toLowerCase()
        return /\b1\b/.test(title) && !/\b(?:2|3|6)\b/.test(title)
      }) ||
      this.variants[0] ||
      null
    )
  }

  getVisibleSellingPlanSelect() {
    return this.querySelector('[data-product-selling-plans] [data-selling-plan-group]:not(.hidden) select')
  }

  getSelectedSellingPlanId() {
    const select = this.getVisibleSellingPlanSelect()
    if (!select || select.disabled) return null
    const id = Number.parseInt(select.value, 10)
    return Number.isFinite(id) ? id : null
  }

  updateAvailableSellingPlans(variant) {
    if (!variant) return
    this.querySelectorAll('[data-selling-plan-group]').forEach((group) => {
      const groupId = group.dataset.sellingPlanGroup
      const available = (variant.selling_plan_allocations || []).some(
        (allocation) => allocation.selling_plan_group_id === groupId
      )
      group.classList.toggle('hidden', !available)

      group.querySelectorAll('option').forEach((option) => {
        const planId = Number.parseInt(option.value, 10)
        const planAvailable = (variant.selling_plan_allocations || []).some(
          (allocation) => allocation.selling_plan_id === planId
        )
        option.disabled = !planAvailable
        option.style.display = planAvailable ? '' : 'none'
        if (!planAvailable && option.selected) option.selected = false
      })

      const selected = group.querySelector('option:checked')
      if (!selected || selected.disabled) {
        const firstAvailable = group.querySelector('option:not([disabled])')
        if (firstAvailable) firstAvailable.selected = true
      }
    })
  }

  getSubscriptionAllocation(variant) {
    if (!variant) return null
    const sellingPlanId = this.getSelectedSellingPlanId()
    const allocations = variant.selling_plan_allocations || []
    if (sellingPlanId) {
      const match = allocations.find((allocation) => allocation.selling_plan_id === sellingPlanId)
      if (match) return match
    }
    return allocations[0] || null
  }

  updateSubscribeTitle() {
    const title = this.querySelector('[data-subscription-title]')
    if (!title) return

    const template = title.dataset.titleTemplate || ''
    const select = this.getVisibleSellingPlanSelect()
    const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null
    const percent = option?.dataset.discountPercent || ''
    const hasPercent = percent !== '' && percent !== '0'

    if (template.includes('[percent]')) {
      if (hasPercent) {
        title.textContent = template.replace('[percent]', percent)
      } else {
        title.textContent = template
          .replace(' [percent]%', '')
          .replace('[percent]%', '')
          .replace('[percent]', '')
      }
      return
    }

    if (hasPercent) {
      title.textContent = `${template} ${percent}%`.trim()
    } else {
      title.textContent = template
    }
  }

  getAutomaticDiscountCents() {
    return Number(this.getAttribute('automatic-discount')) || 0
  }

  getAddonTotal() {
    return Array.from(this.querySelectorAll('[name="addon-product"]:checked')).reduce((sum, input) => {
      if (!input.value) return sum
      return sum + (Number.parseInt(input.dataset.price, 10) || 0)
    }, 0)
  }

  // Keep "Total Value Product" copy from main-product-v2-price / sticky nav.
  // A bare textContent write would replace "(Total Value $X)" on connect and every refresh.
  setCompareAtPriceText(el, formatted) {
    if (/\(\s*Total Value/.test(el.innerHTML)) {
      el.innerHTML = el.innerHTML.replace(
        /\(\s*Total Value(?:&nbsp;|\s)*.*?\)/,
        `(Total Value&nbsp;${formatted})`
      )
      return
    }
    if (/\$[\d,]+(?:\.\d{2})?/.test(el.innerHTML)) {
      el.innerHTML = el.innerHTML.replace(/\$[\d,]+(?:\.\d{2})?/, formatted)
      return
    }
    el.textContent = formatted
  }

  update() {
    const variant = this.getSelectedVariant()
    if (!variant) return

    this.setAttribute('variant-id', String(variant.id))
    const idInput = this.querySelector('input[name="id"]')
    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id)

    this.updateAvailableSellingPlans(variant)
    this.syncSellingPlanFromQuantity(variant)

    const isSubscription = this.isSubscription()
    const sellingPlanWrap = this.querySelector('[data-selling-plan-wrap]')
    if (sellingPlanWrap) sellingPlanWrap.hidden = !isSubscription

    const allocation = this.getSubscriptionAllocation(variant)
    const automaticDiscount = this.getAutomaticDiscountCents()
    const subscriptionPrice = allocation ? allocation.price : variant.price
    const oneTimePrice = variant.price
    const oneTimePack = this.isQuantityFirst() ? this.getOneTimePackVariant() || variant : variant
    const oneTimePackPrice = oneTimePack.price
    const displayPrice = Math.max(
      (isSubscription ? subscriptionPrice : oneTimePrice) - automaticDiscount,
      0
    )
    const compareAt = isSubscription
      ? variant.compare_at_price || variant.price || allocation?.compare_at_price || displayPrice
      : variant.compare_at_price || variant.price
    const totalPrice = displayPrice + this.getAddonTotal()
    const hasCompareAt = compareAt > displayPrice

    this.querySelectorAll('[data-subscription-card-price]').forEach((el) => {
      el.textContent = this.formatter.format(subscriptionPrice / 100)
    })
    this.querySelectorAll('[data-one-time-card-price]').forEach((el) => {
      el.textContent = this.formatter.format(oneTimePrice / 100)
    })
    this.querySelectorAll('[data-one-time-atc-price]').forEach((el) => {
      el.textContent = this.formatter.format(oneTimePackPrice / 100)
    })

    document.querySelectorAll('[data-main-product-price]').forEach((el) => {
      el.textContent = this.formatter.format(totalPrice / 100)
    })

    const formattedCompareAt = this.formatter.format(compareAt / 100)
    document.querySelectorAll('[data-main-product-compare-at-price]').forEach((el) => {
      if (hasCompareAt) {
        this.setCompareAtPriceText(el, formattedCompareAt)
        el.classList.remove('hidden')
      } else {
        el.classList.add('hidden')
      }
    })

    document.querySelectorAll('[data-promo-text]').forEach((el) => {
      el.style.display = isSubscription ? '' : 'none'
    })

    this.updateSubscribeTitle()

    const forceOos = this.hasAttribute('data-force-out-of-stock')
    const unavailable = forceOos || variant.available === false
    // Same ownership as setupAddToCart: in-form ATC plus sticky/mobile CTAs.
    // Unrelated [data-add-to-cart] (e.g. Complete Your Routine plus buttons) must
    // keep their own labels and availability.
    document.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      if (!this.isOwnedAddToCartButton(button)) return
      const text = button.querySelector('[data-text]')
      button.toggleAttribute('disabled', unavailable)
      button.classList.toggle('pointer-events-none', unavailable)
      button.classList.toggle('cursor-not-allowed', unavailable)
      if (!text) return
      if (unavailable) {
        text.textContent = 'SOLD OUT'
      } else if (this.isQuantityFirst()) {
        text.textContent = 'Add to Cart'
      } else {
        text.textContent = isSubscription ? 'Subscribe & Save' : 'Add to Cart'
      }
    })

    this.querySelectorAll('[data-add-to-cart-one-time]').forEach((button) => {
      const oneTimeUnavailable =
        this.hasAttribute('data-force-out-of-stock') || oneTimePack.available === false
      button.toggleAttribute('disabled', oneTimeUnavailable)
      button.classList.toggle('pointer-events-none', oneTimeUnavailable)
      button.classList.toggle('cursor-not-allowed', oneTimeUnavailable)
    })
  }

  syncSellingPlanFromQuantity(variant) {
    if (!this.isQuantityFirst() || !variant) return
    const months = Number.parseInt(
      this.querySelector('[data-product-options] input:checked')?.dataset.quantityMonths || '',
      10
    )
    const select = this.getVisibleSellingPlanSelect()
    if (!select || !months) return

    const needlesByMonths = {
      1: ['4 week', 'every month', '1 month'],
      2: ['8 week', '2 month'],
      3: ['3 month'],
      6: ['6 month'],
    }
    const needles = needlesByMonths[months] || []
    const match = [...select.options].find((option) => {
      if (option.disabled) return false
      const label = option.textContent.toLowerCase()
      return needles.some((needle) => label.includes(needle))
    })
    if (match) select.value = match.value
  }

  getSelectedAddons() {
    return Array.from(this.querySelectorAll('[name="addon-product"]:checked'))
      .filter((input) => input instanceof HTMLInputElement && input.value !== '')
      .map((input) => ({
        quantity: Number.parseInt(input.dataset.quantity || '1', 10) || 1,
        id: Number(input.value),
        properties: { _addon: true },
      }))
  }

  addToCart(button, { oneTime = false } = {}) {
    const variant = oneTime && this.isQuantityFirst() ? this.getOneTimePackVariant() : this.getSelectedVariant()
    if (!variant) return

    const parentVariantId = Number(variant.id)
    const item = { quantity: 1, id: parentVariantId }
    if (!oneTime && this.isSubscription()) {
      const sellingPlanId = this.getSelectedSellingPlanId()
      if (sellingPlanId) item.selling_plan = sellingPlanId
    }

    const items =
      oneTime && this.isQuantityFirst()
        ? [item]
        : [item, ...this.getSelectedAddons().map((addon) => ({ ...addon, parent_id: parentVariantId }))]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items:
          this.dataset.bundle === 'true'
            ? nestBundleItems(window.igApplyCartNesting(items.reverse()))
            : window.igApplyCartNesting(items.reverse()),
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json()
          return alert(error.description || error.message)
        }
        window.HybridCart.open()
      })
      .catch((error) => {
        console.error('[subscription-product-form] Add to cart failed', error)
      })
      .finally(() => {
        delete button.dataset.loading
      })
  }
}

if (!customElements.get('subscription-product-form')) {
  customElements.define('subscription-product-form', SubscriptionProductForm)
}
